import { media } from "../../design/breakpoints";
/**
 * SensorDetailV2.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Full detail screen for water-level sensors (Sump LoRa, OHT LoRa, etc.)
 * Route: /sensor/:sensor_id/:sensor_type
 *
 * Replaces the old SensorData component for water-level type sensors.
 * Design matches sump_production_fixed.html.
 *
 * API calls:
 *   GET /sensor/reading?sensor_id=X&type=Y          → live reading + thresholds
 *   GET /sensor/date-wise-report?sensor_id=X&...    → chart data
 *   PUT /sensor/update_threshold  (placeholder)     → save min/max
 */

import axios from "axios";
import { BiDownload } from "react-icons/bi";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useHistory, useParams } from "react-router-dom";
import styled, { css } from "styled-components";
import { GlobalState } from "../../GlobalState";
import { formatOfflineDuration } from "../utils/formatOfflineDuration";
import { literToKiloliter, capitalizeFirstLetter } from "../utils/utils";
import moment from "moment";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import PullToRefresh from "../utils/PullToRefresh";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

/* ─── Palette ───────────────────────────────────────────────────────────────── */
const GREEN = "#237A35";
const LT_GREEN = "rgba(35,122,53,0.10)";
const AMBER = "#F59E0B";
const RED_ALERT = "#EA580C";
const GRAY_GRID = "#F3F4F6";

/* ─── Styled Components ─────────────────────────────────────────────────────── */
const Screen = styled.div`
  background: #F4F6F9;
  min-height: 100vh;
  font-family: 'Inter', -apple-system, sans-serif;

  @media print {
    background: #fff;
  }
`;


/* Nav */
const Nav = styled.div`
  background: #fff;
  padding: 14px 18px;
  display: flex;
  align-items: center;
  gap: 11px;
  border-bottom: 1px solid #EAECEF;
  position: sticky;
  top: 0;
  z-index: 30;
`;

const BackBtn = styled.button`
  width: 36px; height: 36px;
  border-radius: 50%;
  border: 1.5px solid #E5E7EB;
  background: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 18px;
  cursor: pointer;
  color: #374151;
  flex-shrink: 0;
  &:hover { border-color: ${GREEN}; color: ${GREEN}; }
`;

const NavInfo = styled.div`
  flex: 1; min-width: 0;
`;

const NavEyebrow = styled.div`
  font-size: 11px; color: #4B5563; margin-bottom: 2px;
`;

const NavTitle = styled.div`
  font-size: 17px; font-weight: 700; color: #111;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

const NavDl = styled.button`
  width: 34px; height: 34px;
  border-radius: 9px;
  border: none;
  background: ${GREEN};
  color: #fff;
  font-size: 16px;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
`;


/* Fixed Header */
const FixedHeader = styled.div`
  background: #fff;
  border-bottom: 1px solid #EAECEF;
`;

const HeroSplit = styled.div`
  display: flex;
  padding: 18px 18px 0;
  gap: 0;
`;

/* Gauge */
const GaugeCol = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const GaugeWrap = styled.div`
  position: relative;
  width: 118px; height: 118px;
`;

const GaugeSvg = styled.svg`
  width: 118px; height: 118px;
`;

const GaugeCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
`;

const GaugePct = styled.div`
  font-size: 28px; font-weight: 700; color: #111; line-height: 1; letter-spacing: -1.5px;
  span { font-size: 14px; font-weight: 400; color: #4B5563; }
`;

const GaugeDot = styled.div`
  width: 7px; height: 7px; border-radius: 50%;
  background: ${({ $status }) =>
    $status === "online" ? "#16A34A" : $status === "faulty" ? AMBER : "#94A3B8"};
  margin-top: 3px;
`;

const GaugeStatus = styled.div`
  font-size: 9px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase;
  color: ${({ $status }) =>
    $status === "online" ? "#16A34A" : $status === "faulty" ? "#D97706" : "#6B7280"};
`;

const GaugeLegend = styled.div`
  display: flex; gap: 10px; margin-top: 7px;
`;

const LegendItem = styled.div`
  display: flex; align-items: center; gap: 3px;
  font-size: 9px; color: #4B5563;
`;

const LegendDot = styled.div`
  width: 6px; height: 6px; border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

/* Stats column */
const StatsCol = styled.div`
  flex: 1;
  padding-left: 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-bottom: 4px;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  padding: 5px 0;
  border-bottom: 1px solid #F3F4F6;
  min-height: 28px;
  &:last-child { border-bottom: none; padding-bottom: 0; }
`;

const StatDot = styled.div`
  width: 8px; height: 8px; border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0; margin-right: 8px;
`;

const StatLabel = styled.span`
  font-size: 11px; color: #4B5563; flex: 1;
`;

const StatValue = styled.span`
  font-size: 13px; font-weight: 600; color: #111;
  span { font-size: 10px; font-weight: 400; color: #4B5563; }
`;

/* Inline threshold editing */
const ThresholdView = styled.div`
  display: flex; align-items: center; gap: 5px; cursor: pointer;
  &:hover span:first-child { color: ${GREEN}; }
  &:hover .edit-hint { opacity: 1; }
`;

const EditHint = styled.span`
  font-size: 9px; color: ${GREEN}; opacity: 0;
  transition: opacity .15s; font-weight: 500;
`;

const ThresholdEditor = styled.div`
  display: ${({ $open }) => $open ? "flex" : "none"};
  align-items: center; gap: 4px; flex: 1; justify-content: flex-end;
`;

const ThresholdInput = styled.input`
  width: 52px; height: 26px;
  border: 1.5px solid ${GREEN}; border-radius: 6px;
  font-size: 13px; font-weight: 600; font-family: inherit;
  color: #111; text-align: center;
  background: #F0FDF4; padding: 0 4px; outline: none;
`;

const SaveBtn = styled.button`
  height: 26px; padding: 0 9px;
  background: ${GREEN}; color: #fff;
  border: none; border-radius: 6px;
  font-size: 11px; font-weight: 600; font-family: inherit;
  cursor: pointer; white-space: nowrap;
`;

const CancelBtn = styled.button`
  height: 26px; width: 26px;
  background: #F3F4F6; color: #4B5563;
  border: none; border-radius: 6px;
  font-size: 13px; cursor: pointer; flex-shrink: 0;
`;

/* Tank strip + status bar */
const StatusBar = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 18px;
  background: #F9FAFB;
  border-top: 1px solid #EAECEF;
`;


const OnlinePill = styled.div`
  display: flex; align-items: center; gap: 5px;
  padding: 4px 11px; border-radius: 20px;
  background: ${({ $status }) =>
    $status === "online" ? "#DCFCE7" :
      $status === "faulty" ? "rgba(245,158,11,.12)" : "#F3F4F6"};
`;

const OnlineDot = styled.div`
  width: 6px; height: 6px; border-radius: 50%;
  background: ${({ $status }) =>
    $status === "online" ? "#16A34A" :
      $status === "faulty" ? AMBER : "#94A3B8"};
`;

const OnlineText = styled.span`
  font-size: 11px; font-weight: 600;
  color: ${({ $status }) =>
    $status === "online" ? "#166534" :
      $status === "faulty" ? "#D97706" : "#6B7280"};
`;

const SyncText = styled.span`
  font-size: 11px; color: #4B5563;
  span { font-weight: 600; color: #374151; }
`;

/* Tab bar */
const TabBar = styled.div`
  display: flex;
  background: #fff;
  border-bottom: 2px solid #EAECEF;
`;

const Tab = styled.button`
  flex: 1; padding: 12px 4px;
  border: none; background: none;
  font-size: 13px; font-weight: 500;
  font-family: inherit;
  cursor: pointer; color: #4B5563;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: color .15s;
  ${({ $active }) => $active && css`
    color: ${GREEN};
    border-bottom-color: ${GREEN};
    font-weight: 600;
  `}
`;

/* Body */
const Body = styled.div`
  background: #F4F6F9;
`;

/* Filter row */
const FilterRow = styled.div`
  display: flex; align-items: center; gap: 8px;
  padding: 10px 18px;
  background: #fff;
  border-bottom: 1px solid #EAECEF;
  border-top: 1px solid #EAECEF;
  margin-top: 8px;
`;

const PillTabs = styled.div`
  display: flex; gap: 4px;
`;

const PillTab = styled.button`
  padding: 5px 11px;
  border-radius: 20px;
  border: 1.5px solid #E5E7EB;
  font-size: 11px; font-weight: 500;
  font-family: inherit;
  cursor: pointer; background: #fff; color: #4B5563;
  transition: all .12s;
  ${({ $active }) => $active && css`
    background: #0D3318;
    color: #fff;
    border-color: #0D3318;
  `}
`;

const DateLabel = styled.div`
  display: flex; align-items: center; gap: 5px;
  background: #F9FAFB; border: 1.5px solid #E5E7EB;
  border-radius: 20px; padding: 5px 11px;
  font-size: 11px; font-weight: 500; color: #374151;
  margin-left: auto; cursor: pointer; white-space: nowrap;
`;


/* Range picker */
const RangePicker = styled.div`
  display: ${({ $open }) => $open ? "flex" : "none"};
  align-items: center; gap: 6px;
  padding: 8px 18px;
  background: #fff;
  border-bottom: 1px solid #EAECEF;
  flex-wrap: wrap;
`;

const RpLabel = styled.span`
  font-size: 11px; font-weight: 600; color: #4B5563;
  letter-spacing: .06em; text-transform: uppercase; white-space: nowrap;
`;

const RpInput = styled.input`
  height: 30px;
  border: 1.5px solid #E5E7EB; border-radius: 8px;
  font-size: 11px; font-weight: 500; font-family: inherit;
  color: #374151; padding: 0 8px;
  background: #F9FAFB; cursor: pointer; outline: none;
  &:focus { border-color: ${GREEN}; background: #F0FDF4; }
`;

const RpApply = styled.button`
  height: 30px; padding: 0 12px;
  background: ${GREEN}; color: #fff;
  border: none; border-radius: 8px;
  font-size: 11px; font-weight: 600; font-family: inherit;
  cursor: pointer;
`;

/* Range navigator */
const RangeStrip = styled.div`
  display: ${({ $visible }) => $visible ? "flex" : "none"};
  align-items: center; justify-content: space-between;
  padding: 10px 18px 0;
`;

const RangeArrow = styled.button`
  width: 32px; height: 32px;
  border-radius: 8px; border: 1.5px solid #E5E7EB;
  background: #fff; color: #374151;
  font-size: 16px; cursor: pointer; flex-shrink: 0;
`;

const RangeCenter = styled.div`
  text-align: center; flex: 1;
`;

const RangeLbl = styled.div`
  font-size: 13px; font-weight: 600; color: #111;
`;

const RangeSub = styled.div`
  font-size: 10px; color: #4B5563; margin-top: 1px;
`;

/* Chart card */
const ChartCard = styled.div`
  background: #fff;
  border-top: 1px solid #EAECEF;
  border-bottom: 1px solid #EAECEF;
  padding: 14px 18px 16px;
  margin-top: 10px;
`;

const ChartHeader = styled.div`
  display: flex; align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 14px; gap: 8px;
`;

const ChartTitle = styled.div`
  font-size: 14px; font-weight: 600; color: #111;
`;

const ChartSub = styled.div`
  font-size: 11px; color: #4B5563; margin-top: 2px;
`;

const KpiChip = styled.div`
  text-align: right;
  background: #F0FDF4; border: 1px solid #BBF7D0;
  border-radius: 10px; padding: 6px 12px; flex-shrink: 0;
`;

const KpiVal = styled.div`
  font-size: 18px; font-weight: 700; color: #166534; line-height: 1;
  span { font-size: 10px; font-weight: 400; }
`;

const KpiLbl = styled.div`
  font-size: 9px; color: #16A34A; letter-spacing: .05em; text-transform: uppercase; margin-top: 1px;
`;

const ChartWrap = styled.div`
  height: 200px; position: relative;
`;

const AmPmStrip = styled.div`
  display: ${({ $visible }) => $visible ? "flex" : "none"};
  padding: 5px 0 0; gap: 0;
`;

const AmLabel = styled.div`
  flex: 1; font-size: 10px; font-weight: 600; color: #EF4444;
  text-align: center; border-right: 1.5px solid #E5E7EB; padding-right: 8px;
`;

const PmLabel = styled.div`
  flex: 1; font-size: 10px; font-weight: 600; color: #2563EB;
  text-align: center; padding-left: 8px;
`;

/* Insight chips */
const InsightsSection = styled.div`
  padding: 10px 18px 0;
  background: #fff;
  border-top: 1px solid #EAECEF;
  border-bottom: 1px solid #EAECEF;
  margin-top: 8px;
`;

const InsightsLbl = styled.div`
  font-size: 11px; font-weight: 600; color: #4B5563;
  letter-spacing: .07em; text-transform: uppercase; margin-bottom: 10px;
`;

const InsightsScroll = styled.div`
  display: flex; gap: 8px; overflow-x: auto; padding-bottom: 14px;
   justify-content: center;
  &::-webkit-scrollbar { display: none; }
`;

const Chip = styled.div`
  flex-shrink: 0; width: 130px;
  border-radius: 12px; padding: 12px 13px;
  border: 1.5px solid #E5E7EB; background: #F9FAFB;
  ${({ $cls }) => $cls === "good" && css`background:#F0FDF4;border-color:#BBF7D0;`}
  ${({ $cls }) => $cls === "warn" && css`background:#FEF9C3;border-color:#FDE68A;`}
  ${({ $cls }) => $cls === "info" && css`background:#EFF6FF;border-color:#BFDBFE;`}

  ${media.max480} {
    width: 108px;
    padding: 5px 6px;
    border-radius: 10px;
  }
`;

const ChipLbl = styled.div`
  font-size: 9px; font-weight: 600; color: #4B5563;
  letter-spacing: .06em; text-transform: uppercase; margin-bottom: 6px;

  ${media.max480} {
    font-size: 8px;
    margin-bottom: 4px;
  }
`;

const ChipVal = styled.div`
  font-size: 20px; font-weight: 700; color: #111; line-height: 1;
  span { font-size: 11px; font-weight: 400; color: #4B5563; }

  ${media.max480} {
    font-size: 16px;
    span { font-size: 9px; }
  }
`;

const ChipSub = styled.div`
  font-size: 10px; color: #4B5563; margin-top: 4px;

  ${media.max480} {
    font-size: 9px;
    margin-top: 3px;
  }
`;

const ChipBadge = styled.div`
  display: inline-flex; align-items: center; gap: 3px;
  font-size: 9px; font-weight: 600; padding: 2px 7px;
  border-radius: 6px; margin-top: 5px;
  ${({ $bcls }) => $bcls === "ib-g" && css`background:#DCFCE7;color:#166534;`}
  ${({ $bcls }) => $bcls === "ib-w" && css`background:#FEF3C7;color:#92400E;`}
  ${({ $bcls }) => $bcls === "ib-b" && css`background:#DBEAFE;color:#1E40AF;`}

  ${media.max480} {
    font-size: 8px;
    padding: 1px 5px;
    margin-top: 4px;
  }
`;

const Spacer = styled.div`
  height: 20px; background: #F4F6F9;
`;

/* Summary table */
const SummaryTable = styled.div`
  background: #fff;
  border-top: 1px solid #EAECEF;
  border-bottom: 1px solid #EAECEF;
  margin-top: 8px;
`;

const TableHeader = styled.div`
  display: flex; padding: 10px 18px;
  border-bottom: 1px solid #EAECEF;
`;

const ColHead = styled.span`
  font-size: 10px; font-weight: 600; color: #4B5563;
  letter-spacing: .06em; text-transform: uppercase;
  flex: ${({ $first }) => $first ? "1.4" : "1"};
  text-align: ${({ $first }) => $first ? "left" : "right"};
`;

const TableRow = styled.div`
  display: flex; align-items: center;
  padding: 10px 18px;
  border-bottom: 1px solid #F3F4F6;
  &:last-child { border-bottom: none; }
  background: ${({ $hi }) => $hi ? "#F0FDF4" : "transparent"};
`;

const RowDay = styled.div`
  flex: 1.4; font-size: 12px; font-weight: 500; color: #374151;
`;

const RowCell = styled.div`
  flex: 1; text-align: right; font-size: 12px; font-weight: 600;
  color: ${({ $cls }) =>
    $cls === "good" ? "#166534" :
      $cls === "warn" ? "#D97706" :
        $cls === "muted" ? "#9CA3AF" : "#111"};
`;

const LoadingBox = styled.div`
  padding: 40px; text-align: center; color: #4B5563;
  font-size: 14px;
`;

/* ─── Print styles ──────────────────────────────────────────────────────── */


/* ─── HydroSense Timeline ────────────────────────────────────────────────── */
const TimelineWrap = styled.div`
  padding: 0 18px 16px;
  background: #fff;
  border-top: 1px solid #EAECEF;
  margin-top: 10px;
`;

const TimelineHeader = styled.div`
  display: flex;
  padding: 8px 0 6px;
  border-bottom: 1px solid #F3F4F6;
  margin-bottom: 8px;
`;

const TimelineDayCol = styled.div`
  width: 64px;
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 600;
  color: #4B5563;
  letter-spacing: .04em;
  text-transform: uppercase;
`;

const TimelineHoursCol = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
`;

const TimelineHourLabel = styled.span`
  font-size: 9px;
  color: #4B5563;
  font-weight: 500;
`;

const TimelineRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 4px;
`;

const TimelineDayLabel = styled.div`
  width: 64px;
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  color: #374151;
`;

const TimelineBar = styled.div`
  flex: 1;
  height: 18px;
  background: #F3F4F6;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  position: relative;
`;

const TimelineSegment = styled.div`
  height: 100%;
  background: ${({ $isFlow }) => $isFlow ? "#237A35" : "#E5E7EB"};
  transition: background .1s;
  cursor: default;
  &:hover { opacity: .85; }
`;

const TimelineLegend = styled.div`
  display: flex;
  gap: 14px;
  padding: 8px 0 0;
`;

const LegendChip = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: #4B5563;
`;

const LegendSwatch = styled.div`
  width: 12px;
  height: 10px;
  border-radius: 2px;
  background: ${({ $isFlow }) => $isFlow ? "#237A35" : "#E5E7EB"};
  border: ${({ $isFlow }) => $isFlow ? "none" : "1px solid #D1D5DB"};
`;

/* ─── Gauge SVG component ────────────────────────────────────────────────── */
function GaugeSvgComponent({ pct, minPct, maxPct }) {
  const TRACK = 226;
  const fill = Math.round((Math.min(Math.max(pct, 0), 120) / 100) * TRACK);
  const minOff = -(47 + Math.round((minPct / 100) * TRACK));
  const maxOff = -(47 + Math.round((maxPct / 100) * TRACK));
  // Gauge arc colour matches bar colour logic
  const gaugeColor = pct > 100 || pct > maxPct ? "#F59E0B"
    : pct < minPct ? "#EF4444"
      : GREEN;

  return (
    <GaugeSvg viewBox="0 0 118 118">
      {/* Track */}
      <circle cx="59" cy="59" r="48" fill="none" stroke="#F0F0F0" strokeWidth="11"
        strokeDasharray="226 377" strokeDashoffset="-47" strokeLinecap="round" />
      {/* Fill — colour changes based on threshold state */}
      <circle cx="59" cy="59" r="48" fill="none" stroke={gaugeColor} strokeWidth="11"
        strokeDasharray={`${Math.max(fill, 0)} 377`} strokeDashoffset="-47" strokeLinecap="round" />
      {/* Min marker */}
      <circle cx="59" cy="59" r="48" fill="none" stroke={RED_ALERT} strokeWidth="11"
        strokeDasharray="2 375" strokeDashoffset={minOff} />
      {/* Max marker */}
      <circle cx="59" cy="59" r="48" fill="none" stroke={AMBER} strokeWidth="11"
        strokeDasharray="2 375" strokeDashoffset={maxOff} />
    </GaugeSvg>
  );
}

/* ─── Insight chips helper ───────────────────────────────────────────────── */
function InsightChips({ chips }) {
  return (
    <InsightsScroll>
      {chips.map((c, i) => (
        <Chip key={i} $cls={c.cls}>
          <ChipLbl>{c.label}</ChipLbl>
          <ChipVal>{c.val}<span> {c.unit}</span></ChipVal>
          <ChipSub>{c.sub}</ChipSub>
          {c.badge && <ChipBadge $bcls={c.bcls}>{c.badge}</ChipBadge>}
        </Chip>
      ))}
    </InsightsScroll>
  );
}

/* ─── Chart helpers ──────────────────────────────────────────────────────── */
function greenShade(v, max) {
  const r = v / max;
  return r > 0.85 ? "#166534" : r > 0.65 ? "#237A35" : r > 0.45 ? "#52B96A" : "#BBF7D0";
}

/* Amber shades for the "consumed" (water leaving the tank) view */
function amberShade(v, max) {
  const r = v / max;
  return r > 0.85 ? "#C2410C" : r > 0.65 ? "#EA580C" : r > 0.45 ? "#FB923C" : "#FED7AA";
}


/* ─── Main Component ─────────────────────────────────────────────────────── */
/* ─── HydroSense Day-Row Timeline Component ──────────────────────────────── */
function HydroTimeline({ timestamps, datas }) {
  if (!timestamps || !datas || !timestamps.length) return null;

  const SLOTS = 24;
  const byDay = {};

  // Track value + exact first/last timestamp per hour-slot
  timestamps.forEach((t, i) => {
    const m = moment(t);
    const day = m.format("YYYY-MM-DD");
    const hour = m.hour();
    const val = parseFloat(datas[i]) >= 1 ? 1 : 0;
    if (!byDay[day]) {
      byDay[day] = Array.from({ length: SLOTS }, () => ({
        val: null, first: null, last: null
      }));
    }
    const slot = byDay[day][hour];
    if (slot.val === null) { slot.val = val; slot.first = t; slot.last = t; }
    else {
      if (val === 1) slot.val = 1;
      slot.last = t;
    }
  });

  const days = Object.keys(byDay).sort();
  const hourLabels = ["12a", "", "2", "", "4", "", "6", "", "8", "", "10", "", "12p", "", "2", "", "4", "", "6", "", "8", "", "10", ""];

  const fmtTime = (ts) => ts ? moment(ts).format("h:mm A") : null;

  // Build tooltip for a slot: "Flow: 4:00 AM – 4:58 AM" or "Dry: 9:00 AM – 9:55 AM"
  const slotTitle = (slot) => {
    if (!slot || slot.val === null) return "No data";
    const label = slot.val === 1 ? "Flow" : "Dry";
    const start = fmtTime(slot.first);
    const end = fmtTime(slot.last);
    if (start && end && start !== end) return `${label}: ${start} – ${end}`;
    if (start) return `${label}: ${start}`;
    return label;
  };

  return (
    <TimelineWrap>
      {/* AM / PM band */}
      <div style={{ display: "flex", paddingLeft: 64, marginBottom: 2 }}>
        <div style={{
          flex: "0 0 50%", textAlign: "center",
          fontSize: 9, fontWeight: 700, color: "#2563EB",
          letterSpacing: ".08em", textTransform: "uppercase",
          borderRight: "1px solid #E5E7EB", paddingBottom: 2
        }}>● AM</div>
        <div style={{
          flex: "0 0 50%", textAlign: "center",
          fontSize: 9, fontWeight: 700, color: "#EF4444",
          letterSpacing: ".08em", textTransform: "uppercase",
          paddingBottom: 2
        }}>● PM</div>
      </div>

      {/* Hour axis header */}
      <TimelineHeader>
        <TimelineDayCol>Day</TimelineDayCol>
        <TimelineHoursCol>
          {hourLabels.map((lbl, i) => (
            <TimelineHourLabel
              key={i}
              style={{ color: i < 12 ? "#2563EB" : "#EF4444", fontWeight: lbl ? 600 : 400 }}
            >
              {lbl}
            </TimelineHourLabel>
          ))}
        </TimelineHoursCol>
      </TimelineHeader>

      {/* One row per day */}
      {days.map(day => {
        const slots = byDay[day];
        return (
          <TimelineRow key={day}>
            <TimelineDayLabel>{moment(day).format("D MMM")}</TimelineDayLabel>
            <TimelineBar>
              {slots.map((slot, i) => (
                <TimelineSegment
                  key={i}
                  $isFlow={slot.val === 1}
                  style={{
                    width: `${100 / SLOTS}%`,
                    /* Thin separator at noon boundary */
                    borderRight: i === 11 ? "1.5px solid rgba(0,0,0,0.15)" : "none",
                  }}
                  title={slotTitle(slot)}
                />
              ))}
            </TimelineBar>
          </TimelineRow>
        );
      })}

      <TimelineLegend>
        <LegendChip><LegendSwatch $isFlow /> Flow detected</LegendChip>
        <LegendChip><LegendSwatch /> Dry / No flow</LegendChip>
      </TimelineLegend>
    </TimelineWrap>
  );
}

export default function SensorDetailV2() {
  const { sensor_id, sensor_type } = useParams();
  const history = useHistory();
  /* HydroSense = binary water-presence sensor (value 0 or 1) */
  const isHydroSense = sensor_type?.toLowerCase().includes("water sense") ||
    sensor_type?.toLowerCase().includes("watersense");
  const state = useContext(GlobalState);

  /* ── Live reading state ── */
  const [reading, setReading] = useState(null);
  const [readLoading, setReadLoading] = useState(true);

  /* ── Tank dimension state (from /sensor/data) ── */
  const [dimension, setDimension] = useState(null);

  /* ── Threshold edit state ── */
  const [thresholds, setThresholds] = useState({ min: 20, max: 80 });
  const [editMin, setEditMin] = useState(false);
  const [editMax, setEditMax] = useState(false);
  const [editMinVal, setEditMinVal] = useState("");
  const [editMaxVal, setEditMaxVal] = useState("");

  /* ── HydroSense wait-time edit state (from /sensor/data → wait_time) ── */
  const [editWaitTime, setEditWaitTime] = useState("");
  const [savingWait, setSavingWait] = useState(false);
  const [waitMsg, setWaitMsg] = useState("");
  const [waitErr, setWaitErr] = useState(false);

  /* ── Tab + filter state ── */
  const [activeTab, setActiveTab] = useState("trend");
  const [trendMode, setTrendMode] = useState("today");
  const [volMode, setVolMode] = useState("today");
  // Volume metric: null = follow tank default (Sump→filled, OHT→consumed); set by toggle
  const [volMetricOverride, setVolMetricOverride] = useState(null);

  /* ── Date range picker ── */
  const today = moment().format("YYYY-MM-DD");
  /* Single date picker for "date" mode */
  const [tSingleDate, setTSingleDate] = useState(moment().subtract(1, "days").format("YYYY-MM-DD"));
  const [vSingleDate, setVSingleDate] = useState(moment().subtract(1, "days").format("YYYY-MM-DD"));
  const [tFrom, setTFrom] = useState(moment().subtract(14, "days").format("YYYY-MM-DD"));
  const [tTo, setTTo] = useState(today);
  const [vFrom, setVFrom] = useState(moment().subtract(14, "days").format("YYYY-MM-DD"));
  const [vTo, setVTo] = useState(today);

  /* ── Chart data state ── */
  const [chartData, setChartData] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);

  /* ── Fetch live reading ── */
  useEffect(() => {
    /* One-time fetch of sensor metadata (tank shape, capacity, thresholds) */
    async function fetchDimension() {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API}/sensor/data?sensor_id=${sensor_id}`,
          { headers: { Authorization: state.token[0] } }
        );
        const d = res.data;
        setDimension(d);
        if (d?.wait_time != null) setEditWaitTime(String(d.wait_time));
        // Seed thresholds from sensor metadata if available
        // /sensor/data uses lower_limit / upper_limit
        if (d?.lower_limit != null) {
          setThresholds({
            min: parseInt(d.lower_limit) || 20,
            max: parseInt(d.upper_limit) || 80,
          });
        }
      } catch (e) {
        console.warn("dimension fetch (non-critical):", e.message);
      }
    }
    fetchDimension();

    async function fetchReading() {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API}/sensor/reading?sensor_id=${sensor_id}&&type=${sensor_type}`,
          { headers: { Authorization: state.token[0] } }
        );
        const d = res.data;
        setReading(d);
        // Thresholds already seeded from /sensor/data; reading is live data only
      } catch (e) {
        console.error("reading fetch:", e);
      } finally {
        setReadLoading(false);
      }
    }
    fetchReading();
    const iv = setInterval(fetchReading, 10000);
    return () => clearInterval(iv);
  }, [sensor_id, sensor_type, state.token]);

  /* ── Build date range from mode ── */
  const buildRange = useCallback((mode, fromDate, toDate) => {
    const fmt = "YYYY-MM-DD HH:mm:ss";
    switch (mode) {
      case "today":
        return {
          start: moment().startOf("day").format(fmt),
          end: moment().endOf("day").format(fmt),
        };
      case "date":
        // fromDate holds the user-selected single date for "date" mode
        return {
          start: moment(fromDate).startOf("day").format(fmt),
          end: moment(fromDate).endOf("day").format(fmt),
        };
      case "week":
        // fromDate is the window start; if it's the default tFrom (14 days ago),
        // fall back to the natural "last 7 days" window
        if (fromDate && fromDate !== moment().subtract(14, "days").format("YYYY-MM-DD")) {
          return {
            start: moment(fromDate).startOf("day").format(fmt),
            end: moment(toDate || fromDate).add(6, "days").endOf("day").format(fmt),
          };
        }
        return {
          start: moment().subtract(6, "days").startOf("day").format(fmt),
          end: moment().endOf("day").format(fmt),
        };
      case "range":
        return {
          start: moment(fromDate).startOf("day").format(fmt),
          end: moment(toDate).endOf("day").format(fmt),
        };
      default:
        return {
          start: moment().startOf("day").format(fmt),
          end: moment().endOf("day").format(fmt),
        };
    }
  }, []);

  /* ── Fetch chart data ── */
  const fetchChart = useCallback(
    async (mode, fromDate, toDate) => {
      setChartLoading(true);
      setChartData(null);
      const { start, end } = buildRange(mode, fromDate, toDate);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API}/sensor/date-wise-report` +
          `?sensor_id=${sensor_id}` +
          `&sensor_type=${encodeURIComponent(sensor_type)}` +
          `&start_date=${start}&end_date=${end}`,
          { headers: { Authorization: state.token[0] } }
        );
        setChartData({ ...res.data, mode, start, end });
      } catch (e) {
        console.error("chart fetch:", e);
        setChartData(null);
      } finally {
        setChartLoading(false);
      }
    },
    [sensor_id, sensor_type, state.token, buildRange]
  );

  /* ── Initial chart load ── */
  useEffect(() => {
    fetchChart(trendMode, tFrom, tTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Tab/mode change triggers fetch ── */
  const handleTrendMode = (m) => {
    setTrendMode(m);
    if (m === "date") {
      fetchChart("date", tSingleDate, tSingleDate);
    } else if (m === "week") {
      // Initialise tFrom/tTo to the actual current week so shiftWindow has correct base
      const wFrom = moment().subtract(6, "days").format("YYYY-MM-DD");
      const wTo = moment().format("YYYY-MM-DD");
      setTFrom(wFrom);
      setTTo(wTo);
      fetchChart("week", wFrom, wTo);
    } else if (m !== "range") {
      fetchChart(m, tFrom, tTo);
    }
  };

  const handleVolMode = (m) => {
    setVolMode(m);
    if (m === "date") {
      fetchChart("date", vSingleDate, vSingleDate);
    } else if (m === "week") {
      const wFrom = moment().subtract(6, "days").format("YYYY-MM-DD");
      const wTo = moment().format("YYYY-MM-DD");
      setVFrom(wFrom);
      setVTo(wTo);
      fetchChart("week", wFrom, wTo);
    } else if (m !== "range") {
      fetchChart(m, vFrom, vTo);
    }
  };

  /* ── PDF download via browser print ── */
  /* ── Export as PNG (captures full page including header + footer) ── */
const screenRef = useRef(null);

const handleDownload = async () => {
  try {
    const html2canvas = (await import("html2canvas")).default;
    const target = screenRef.current || document.body;

    const canvas = await html2canvas(target, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#F4F6F9",
      logging: false,
      onclone: (clonedDoc) => {
        const noscripts = clonedDoc.querySelectorAll("noscript");
        noscripts.forEach((el) => el.remove());
      },
      ignoreElements: (el) => {
        const tagName = el.tagName ? el.tagName.toLowerCase() : "";
        return tagName === "noscript" || tagName === "script";
      },
    });

    const dataUrl = canvas.toDataURL("image/png");

    if (navigator.share && navigator.canShare) {
      try {
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], "vaaripa-report.png", { type: "image/png" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: "Vaaripa Report" });
          return;
        }
      } catch (shareErr) {
        console.warn("Share failed, falling back to download:", shareErr);
      }
    }

    const link = document.createElement("a");
    link.download = `vaaripa-report-${Date.now()}.png`;
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (e) {
    console.error("Screenshot failed:", e);
    alert("Could not generate image: " + e.message);
  }
};

  /* ── Threshold save (placeholder) ── */
  const saveThreshold = async (which) => {
    const val = which === "min" ? parseInt(editMinVal) : parseInt(editMaxVal);
    if (isNaN(val) || val < 0 || val > 100) return;
    if (which === "min" && val >= thresholds.max) { alert(`Min must be less than Max (${thresholds.max}%)`); return; }
    if (which === "max" && val <= thresholds.min) { alert(`Max must be greater than Min (${thresholds.min}%)`); return; }

    const updated = { ...thresholds, [which]: val };
    setThresholds(updated);
    which === "min" ? setEditMin(false) : setEditMax(false);

    try {
      // PLACEHOLDER — replace with real endpoint when available
      await axios.put(
        `${process.env.REACT_APP_API}/sensor/update_limit?sensor_id=${sensor_id}`,
        { lower_limit: updated.min, upper_limit: updated.max },
        { headers: { Authorization: state.token[0] } }
      );
    } catch (e) {
      console.warn("Threshold save placeholder — endpoint not yet wired:", e.message);
    }
  };

  /* ── Derived reading values ── */
  const pct = parseFloat(reading?.volume || 0); // volume = % level
  const hydroValue = parseFloat(reading?.value || 0); // binary 0/1 for HydroSense
  const isFaulty = reading?.raw_data === -1;
  const isOnline = reading?.sensor_status === "online";
  const status = isFaulty ? "faulty" : isOnline ? "online" : "offline";
  const volFilled = parseFloat(reading?.value || 0);  // value = raw litres
  // tank dims live in dimension.dimension.* (nested object from /sensor/data)
  const tankVol = parseFloat(dimension?.dimension?.tank_volume ?? reading?.tank_volume ?? 0);
  const toFill = Math.max(0, tankVol - volFilled);
  const tankShape = capitalizeFirstLetter(
    dimension?.dimension?.tank_shape ?? reading?.tank_shape ?? ""
  );
  const locName = dimension?.location_name || dimension?.location || reading?.location_name || sensor_id;
  const lastSync = reading ? formatOfflineDuration(reading.date, reading.time) : "—";

  /* ── HydroSense wait-time (mins): clamp, dirty check, save ── */
  const clampWait = (v) => {
    if (v === "") return "";
    const n = Math.floor(Number(v));
    if (isNaN(n)) return "";
    return String(Math.max(0, Math.min(999, n)));
  };
  const waitDirty = dimension != null && editWaitTime !== "" &&
    String(editWaitTime) !== String(dimension.wait_time ?? "");
  const saveWaitTime = async () => {
    setSavingWait(true); setWaitMsg(""); setWaitErr(false);
    try {
      await axios.patch(
        `${process.env.REACT_APP_API}/sensor/${sensor_id}`,
        { wait_time: Number(editWaitTime) },
        { headers: { Authorization: state.token[0] } }
      );
      setDimension((prev) => ({ ...(prev || {}), wait_time: Number(editWaitTime) }));
      setWaitMsg("Saved");
      setTimeout(() => setWaitMsg(""), 2500);
    } catch (e) {
      console.error("wait_time save:", e);
      setWaitErr(true); setWaitMsg("Save failed");
    } finally { setSavingWait(false); }
  };

  /* ── Build Chart.js datasets ── */
  function buildTrendDatasets(mode, timestamps, datas) {
    if (!timestamps || !datas) return null;

    /* ── HydroSense: binary 0/1 step chart ────────────────────────────── */
    if (isHydroSense) {
      const labels = timestamps.map(t => {
        const h = moment(t);
        const hr = h.hour();
        const fmt = h.format("h");
        if (hr === 0) return "12a";
        if (hr === 12) return "12p";
        return fmt;
      });
      const amPmColors = timestamps.map(t => moment(t).hour() < 12 ? "#EF4444" : "#2563EB");
      const data = datas.map(v => parseFloat(v) >= 1 ? 1 : 0);
      return {
        chartType: "line",
        isDayView: true,
        isHydro: true,
        labels,
        amPmColors,
        datasets: [{
          data,
          _timestamps: timestamps,  // raw timestamps for exact-time tooltip
          borderColor: "#237A35",
          backgroundColor: (ctx) => {
            // Flow segments: solid green fill; Dry segments: transparent
            // This removes the top-line artifact entirely
            const chart = ctx.chart;
            const { chartArea } = chart;
            if (!chartArea) return "rgba(35,122,53,0.15)";
            return "rgba(35,122,53,0.15)";
          },
          fill: true,
          stepped: true,
          tension: 0,
          pointRadius: 0,
          borderWidth: 0,
        }],
      };
    }

    const isDayView = mode === "today" || mode === "date";

    /* For day view: hourly points, colour by AM/PM */
    if (isDayView) {
      // Label the hour only on its FIRST reading; blank on the rest. This keeps
      // each hour on the axis exactly once (uneven reading cadence used to let
      // two ticks land in the same hour, e.g. "5" twice).
      let _prevH = null;
      const labels = timestamps.map(t => {
        const h = moment(t).hour();
        if (h === _prevH) return "";
        _prevH = h;
        return moment(t).format("h");  // 12hr, no leading zero
      });
      const amPmColors = timestamps.map(t =>
        moment(t).hour() < 12 ? "#EF4444" : "#2563EB"
      );
      return {
        chartType: "line",
        isDayView: true,
        labels,
        amPmColors,
        datasets: [
          {
            data: datas,
            _timestamps: timestamps,  // raw reading times for exact-time tooltip
            borderColor: GREEN,
            backgroundColor: LT_GREEN,
            fill: true,
            tension: 0.42,
            pointRadius: 0,
            borderWidth: 2.5,
          },
          {
            data: Array(datas.length).fill(thresholds.max),
            borderColor: AMBER,
            borderDash: [5, 4],
            borderWidth: 1.5,
            pointRadius: 0,
            fill: false,
            tension: 0,
          },
          {
            data: Array(datas.length).fill(thresholds.min),
            borderColor: RED_ALERT,
            borderDash: [5, 4],
            borderWidth: 1.5,
            pointRadius: 0,
            fill: false,
            tension: 0,
          },
        ],
      };
    }

    /* Multi-day: aggregate by day — avg/min/max */
    const byDay = {};
    timestamps.forEach((t, i) => {
      const v = parseFloat(datas[i]);
      if (!Number.isFinite(v)) return; // skip nulls/invalid
      const day = moment(t).format("YYYY-MM-DD");
      if (!byDay[day]) byDay[day] = [];
      byDay[day].push(v);
    });

    const days = Object.keys(byDay).sort();
    const labels = days.map(d => moment(d).format("D MMM"));
    const avg = days.map(d => byDay[d].length ? Math.round(byDay[d].reduce((a, b) => a + b, 0) / byDay[d].length) : 0);
    const min = days.map(d => byDay[d].length ? Math.round(Math.min(...byDay[d])) : 0);
    const max = days.map(d => byDay[d].length ? Math.round(Math.max(...byDay[d])) : 0);
    const n = days.length;

    return {
      chartType: "line",
      isDayView: false,
      labels,
      datasets: [
        {
          label: "Avg",
          data: avg,
          borderColor: GREEN,
          backgroundColor: LT_GREEN,
          fill: true,
          tension: 0.35,
          pointRadius: n <= 14 ? 4 : 2,
          pointBackgroundColor: GREEN,
          pointBorderColor: "#fff",
          pointBorderWidth: 2,
          borderWidth: 2.5,
        },
        {
          label: "Max",
          data: max,
          borderColor: AMBER,
          borderDash: [5, 4],
          borderWidth: 1.5,
          pointRadius: n <= 14 ? 3 : 0,
          pointBackgroundColor: AMBER,
          fill: false,
          tension: 0.3,
        },
        {
          label: "Min",
          data: min,
          borderColor: RED_ALERT,
          borderDash: [5, 4],
          borderWidth: 1.5,
          pointRadius: n <= 14 ? 3 : 0,
          pointBackgroundColor: RED_ALERT,
          fill: false,
          tension: 0.3,
        },
        {
          data: Array(n).fill(thresholds.max),
          borderColor: "rgba(245,158,11,.25)",
          borderWidth: 1,
          pointRadius: 0,
          fill: false,
          tension: 0,
        },
        {
          data: Array(n).fill(thresholds.min),
          borderColor: "rgba(234,88,12,.25)",
          borderWidth: 1,
          pointRadius: 0,
          fill: false,
          tension: 0,
        },
      ],
      /* computed for insight chips */
      avg: Math.round(avg.reduce((a, b) => a + b, 0) / avg.length),
      minVal: Math.min(...min),
      maxVal: Math.max(...max),
      days: labels,
    };
  }

  /**
   * Volume bars derived from LITRE readings (datas = raw litres).
   *
   * We look at the change between consecutive readings:
   *   positive delta  -> water FILLED   (added to the tank)
   *   negative delta  -> water CONSUMED (drawn from the tank)
   * A bar (2-hr slot, or a day) is the sum of positive deltas (filled) or the
   * sum of |negative deltas| (consumed), chosen by `metric`.
   *
   * Two guards keep the numbers honest:
   *   - JITTER_L deadband: ignore tiny level wiggles (sensor noise).
   *   - adaptive gap: don't count a delta across an abnormally long reading
   *     gap (device offline / reset).
   * The value is NET per interval — simultaneous in+out can't be split from
   * level alone.
   */
  function buildVolDatasets(mode, timestamps, datas, unit, tankVol, metric, litresArr) {
    if (!timestamps || !datas || timestamps.length === 0) return null;
    const isDayView = mode === "today" || mode === "date";
    const isConsumed = metric === "consumed";

    // Prefer the backend litres series (datasLitres) when present — it's the
    // real water volume (docs_i=0). Otherwise fall back to the percentage
    // series and convert via tank capacity.
    const hasLitres = Array.isArray(litresArr) && litresArr.length === timestamps.length;
    const source = hasLitres ? litresArr : datas;

    // Chronological (time, value) points
    const pts = timestamps
      .map((t, i) => ({ t: moment(t), v: parseFloat(source[i]) }))
      .filter(p => p.t.isValid() && Number.isFinite(p.v))
      .sort((a, b) => a.t.valueOf() - b.t.valueOf());
    if (pts.length < 2) return null; // need at least two readings to derive a change

    // Fallback only: the percentage series (0–120) is converted to LITRES using
    // tank capacity so the deadband and totals are in real volume.
    if (!hasLitres) {
      const rawMax = pts.reduce((m, p) => Math.max(m, p.v), 0);
      const looksLikePercent = tankVol > 150 && rawMax <= 120;
      if (looksLikePercent) pts.forEach(p => { p.v = (p.v / 100) * tankVol; });
    }

    // ── Denoise: deadband-follower with dwell (hysteresis) ───────────────
    // HOLD the committed level through small sensor wiggle; only "move" the
    // level when it has shifted from the committed value by >= THRESHOLD_L AND
    // that shift persists for at least DWELL readings. This:
    //   - keeps flat periods flat (no phantom bars),
    //   - preserves WHEN real fills/draws happen (no line-fitting / smearing),
    //   - rejects 1–2 sample spikes that jump and snap back.
    // Both constants are tunable: raise THRESHOLD_L to ignore bigger wobbles;
    // raise DWELL to reject longer spikes (at the cost of reacting a touch later).
    const THRESHOLD_L = tankVol > 0 ? Math.max(8, tankVol * 0.02) : 25;
    const DWELL = 3;
    if (pts.length >= 2) {
      let base = pts[0].v;
      let cand = null;        // candidate new level being confirmed
      let candCount = 0;      // consecutive readings supporting the candidate
      for (let i = 1; i < pts.length; i++) {
        const raw = pts[i].v; // read the real reading first
        if (Math.abs(raw - base) >= THRESHOLD_L) {
          if (cand !== null && Math.sign(raw - base) === Math.sign(cand - base)) {
            candCount++;                 // still moving the same way → build confidence
          } else {
            cand = raw; candCount = 1;   // new candidate direction
          }
          if (candCount >= DWELL) { base = raw; cand = null; candCount = 0; } // commit
        } else {
          cand = null; candCount = 0;    // snapped back within deadband → was a spike
        }
        pts[i].v = base;                 // otherwise hold flat
      }
    }

    // Jitter deadband. The backend series is ALREADY a 10-point moving average,
    // so per-reading noise is minimal and gradual consumption/fill arrives as
    // many small (~1 L) steps. Keep the deadband tiny so a slow drain isn't
    // erased — a large deadband (e.g. % of capacity) would filter every small
    // step and wipe out real gradual movement.
    const JITTER_L = 0.5;

    // Adaptive gap: flag intervals far larger than the typical cadence as
    // offline gaps (don't attribute a delta across them).
    const intervals = [];
    for (let i = 1; i < pts.length; i++) intervals.push(pts[i].t.diff(pts[i - 1].t, "minutes"));
    const sortedIv = [...intervals].sort((a, b) => a - b);
    const medianIv = sortedIv.length ? sortedIv[Math.floor(sortedIv.length / 2)] : 60;
    const MAX_GAP_MIN = Math.max(60, medianIv * 4);

    const applyDelta = (bucket, i) => {
      const gap = pts[i].t.diff(pts[i - 1].t, "minutes");
      if (gap > MAX_GAP_MIN) return;
      const d = pts[i].v - pts[i - 1].v;
      if (Math.abs(d) < JITTER_L) return;
      if (d > 0) bucket.filled += d; else bucket.consumed += -d;
    };

    if (isDayView) {
      const slotMap = {}; // key -> { filled, consumed, isAM, sortKey }
      const slotOf = (m) => {
        const h = m.hour();
        const slotH = Math.floor(h / 2) * 2;
        const to12 = (h24) => (h24 === 0 || h24 === 12) ? "12" : String(h24 > 12 ? h24 - 12 : h24);
        const suffix = slotH < 12 ? "a" : "p";
        const key = `${to12(slotH)}-${to12(slotH + 2)}${suffix}`;
        if (!slotMap[key]) slotMap[key] = { filled: 0, consumed: 0, isAM: h < 12, sortKey: slotH };
        return slotMap[key];
      };
      // Establish slots that have readings (keeps the x-axis continuous)
      pts.forEach(p => slotOf(p.t));
      // Attribute each delta to the slot of the later reading
      for (let i = 1; i < pts.length; i++) applyDelta(slotOf(pts[i].t), i);

      const slots = Object.keys(slotMap).sort((a, b) => slotMap[a].sortKey - slotMap[b].sortKey);
      const isAM = slots.map(s => slotMap[s].isAM);
      const data = slots.map(s => parseFloat((isConsumed ? slotMap[s].consumed : slotMap[s].filled).toFixed(1)));
      const filledTotal = slots.reduce((a, s) => a + slotMap[s].filled, 0);
      const consumedTotal = slots.reduce((a, s) => a + slotMap[s].consumed, 0);
      const baseAM = isConsumed ? "#EA580C" : "#237A35";
      const basePM = isConsumed ? "#FB923C" : "#52B96A";

      return {
        chartType: "bar",
        isDayView: true,
        labels: slots,
        isAM,
        datasets: [{ data, backgroundColor: data.map((v, i) => isAM[i] ? baseAM : basePM), borderRadius: 5, borderSkipped: false }],
        total: (isConsumed ? consumedTotal : filledTotal).toFixed(1),
        filledTotal: filledTotal.toFixed(1),
        consumedTotal: consumedTotal.toFixed(1),
        metric,
        unit,
      };
    }

    /* Multi-day: filled / consumed per calendar day */
    const byDay = {}; // day -> { filled, consumed }
    const dayOf = (m) => {
      const day = m.format("YYYY-MM-DD");
      if (!byDay[day]) byDay[day] = { filled: 0, consumed: 0 };
      return byDay[day];
    };
    pts.forEach(p => dayOf(p.t));
    for (let i = 1; i < pts.length; i++) applyDelta(dayOf(pts[i].t), i);

    const days = Object.keys(byDay).sort();
    const labels = days.map(d => moment(d).format("D MMM"));
    const data = days.map(d => parseFloat((isConsumed ? byDay[d].consumed : byDay[d].filled).toFixed(1)));
    const mx = Math.max(...data, 1);
    const filledTotal = days.reduce((a, d) => a + byDay[d].filled, 0);
    const consumedTotal = days.reduce((a, d) => a + byDay[d].consumed, 0);
    const shade = isConsumed ? amberShade : greenShade;

    return {
      chartType: "bar",
      isDayView: false,
      labels,
      datasets: [{ data, backgroundColor: data.map(v => shade(v, mx)), borderRadius: 5, borderSkipped: false }],
      total: (isConsumed ? consumedTotal : filledTotal).toFixed(1),
      filledTotal: filledTotal.toFixed(1),
      consumedTotal: consumedTotal.toFixed(1),
      metric,
      unit,
    };
  }

  /* ── Trend tooltip config ── */
  function trendTooltipPlugin(isDayView) {
    return {
      filter: (item) => {
        const di = item.datasetIndex;
        if (isDayView) return di === 0;
        return di <= 2; // Avg, Max, Min only
      },
      callbacks: {
        title: (items) => {
          const it = items[0];
          if (isDayView) {
            // Show the actual reading time, not just the hour label
            const rawTs = it?.dataset?._timestamps?.[it.dataIndex];
            return rawTs ? moment(rawTs).format("h:mm A") : (it?.label ?? "");
          }
          return it?.label ?? "";
        },
        label: (ctx) => {
          const di = ctx.datasetIndex;
          const v = ctx.parsed.y;
          if (isDayView) return ` Level: ${v}%`;
          if (di === 0) return ` Avg: ${v}%`;
          if (di === 1) return ` Max: ${v}%`;
          if (di === 2) return ` Min: ${v}%`;
          return null;
        },
      },
    };
  }

  /* ── Mode label helpers ── */
  function modeDateLabel(mode, fromDate, toDate) {
    switch (mode) {
      case "today": return `📅 ${moment().format("D MMM YYYY")}`;
      case "date": return `📅 ${moment(fromDate).format("D MMM YYYY")}`;
      case "week": return `📅 ${moment().subtract(6, "days").format("D MMM")} – ${moment().format("D MMM YYYY")}`;
      case "range": return `📅 ${moment(fromDate).format("D MMM")} – ${moment(toDate).format("D MMM YYYY")}`;
      default: return "";
    }
  }

  function modeRangeLabel(mode, fromDate, toDate) {
    switch (mode) {
      case "week": return { label: `${moment().subtract(6, "days").format("D MMM")} – ${moment().format("D MMM YYYY")}`, sub: "Last 7 days" };
      case "range": return { label: `${moment(fromDate).format("D MMM")} – ${moment(toDate).format("D MMM YYYY")}`, sub: `${Math.round((new Date(toDate) - new Date(fromDate)) / 86400000) + 1} days` };
      default: return null;
    }
  }

  /* ── Insight chips for trend ── */
  function trendChips(mode, ds) {
    if (!ds) return [];
    /* Always compute directly from dataset arrays — avoids stale pre-computed scalars */
    const d0 = (ds.datasets[0]?.data || []).filter(v => Number.isFinite(v));
    if (!d0.length) return [];
    if (ds.isDayView) {
      const avg = Math.round(d0.reduce((a, b) => a + b, 0) / d0.length);
      const max = Math.max(...d0);
      const min = Math.min(...d0);
      return [
        { label: "Day avg", val: avg, unit: "%", sub: "all readings", cls: "good", badge: "Today", bcls: "ib-g" },
        { label: "Peak", val: max, unit: "%", sub: "highest", cls: max > thresholds.max ? "warn" : "", badge: max > thresholds.max ? "⚠ Over max" : "Peak", bcls: max > thresholds.max ? "ib-w" : "ib-g" },
        { label: "Lowest", val: min, unit: "%", sub: "lowest point", cls: min < thresholds.min ? "warn" : "", badge: min < thresholds.min ? "⚠ Under min" : "Lowest", bcls: min < thresholds.min ? "ib-w" : "ib-g" },
      ];
    }
    /* Multi-day: ds.datasets[0]=Avg, [1]=Max, [2]=Min per day */
    const dAvg = (ds.datasets[0]?.data || []).filter(v => Number.isFinite(v));
    const dMax = (ds.datasets[1]?.data || []).filter(v => Number.isFinite(v));
    const dMin = (ds.datasets[2]?.data || []).filter(v => Number.isFinite(v));
    if (!dAvg.length) return [];
    const periodAvg = Math.round(dAvg.reduce((a, b) => a + b, 0) / dAvg.length);
    const highestDay = dMax.length ? Math.max(...dMax) : Math.max(...dAvg);
    const lowestDay = dMin.length ? Math.min(...dMin) : Math.min(...dAvg);
    return [
      { label: "Period avg", val: periodAvg, unit: "%", sub: "across all days", cls: "good", badge: "Avg", bcls: "ib-g" },
      { label: "Highest day", val: highestDay, unit: "%", sub: "max single day", cls: highestDay > thresholds.max ? "warn" : "", badge: highestDay > thresholds.max ? "⚠ Over max" : "Peak", bcls: highestDay > thresholds.max ? "ib-w" : "ib-g" },
      { label: "Lowest day", val: lowestDay, unit: "%", sub: "min single day", cls: lowestDay < thresholds.min ? "warn" : "", badge: lowestDay < thresholds.min ? "⚠ Under min" : "Low", bcls: lowestDay < thresholds.min ? "ib-w" : "ib-g" },
    ];
  }

  function volChips(ds) {
    if (!ds) return [];
    const data = (ds.datasets[0]?.data || []).filter(v => Number.isFinite(v));
    if (!data.length) return [];
    const total = parseFloat(ds.total || 0);
    const peak = Math.max(...data);
    const low = Math.min(...data);
    // literToKiloliter returns a formatted string WITH unit e.g. "4.8 kL"
    // so unit must be "" to avoid double suffix in the chip renderer
    const fmt = (v) => literToKiloliter(parseFloat(v));  // always convert litres→kL
    return [
      { label: "Total", val: fmt(total), unit: "", sub: "all fills", cls: "good", badge: "Total", bcls: "ib-g" },
      { label: "Peak", val: fmt(peak), unit: "", sub: "highest slot/day", cls: "", badge: "Peak", bcls: "ib-g" },
      { label: "Lowest", val: fmt(low), unit: "", sub: "lowest slot/day", cls: "warn", badge: "Low", bcls: "ib-w" },
    ];
  }

  /* ── Summary table for multi-day ── */
  function TrendTable({ ds }) {
    if (!ds || ds.isDayView) return null;
    const avg = ds.datasets[0]?.data || [];
    const max = ds.datasets[1]?.data || [];
    const min = ds.datasets[2]?.data || [];
    return (
      <SummaryTable className="no-print">
        <TableHeader>
          <ColHead $first>Day</ColHead>
          <ColHead>Avg %</ColHead>
          <ColHead>Min %</ColHead>
          <ColHead>Max %</ColHead>
        </TableHeader>
        {ds.labels.map((lbl, i) => (
          <TableRow key={i}>
            <RowDay>{lbl}</RowDay>
            <RowCell $cls={avg[i] >= 65 ? "good" : ""}>{avg[i]}%</RowCell>
            <RowCell $cls="muted">{min[i]}%</RowCell>
            <RowCell $cls={max[i] > thresholds.max ? "warn" : "muted"}>{max[i]}%</RowCell>
          </TableRow>
        ))}
      </SummaryTable>
    );
  }

  function VolTable({ ds }) {
    if (!ds || ds.isDayView) return null;
    const data = ds.datasets[0]?.data || [];
    const total = parseFloat(ds.total || 0);
    const avg = total / data.length;
    return (
      <SummaryTable className="no-print">
        <TableHeader>
          <ColHead $first>Day</ColHead>
          <ColHead>Volume</ColHead>
          <ColHead>Status</ColHead>
        </TableHeader>
        {ds.labels.map((lbl, i) => {
          const v = data[i];
          const ok = v >= avg;
          return (
            <TableRow key={i}>
              <RowDay>{lbl}</RowDay>
              <RowCell $cls="good">{literToKiloliter(v)}</RowCell>
              <RowCell $cls={ok ? "good" : "warn"}>{ok ? "Normal" : "Low"}</RowCell>
            </TableRow>
          );
        })}
      </SummaryTable>
    );
  }

  /* ── Common chart options (trend) ── */
  function trendOptions(isDayView, amPmColors, isHydro, hydroAmPmColors) {
    if (isHydro) return {
      responsive: true, maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: false }, datalabels: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => {
              // Show the hour label as the tooltip title
              const lbl = items[0]?.label || "";
              return lbl ? `Time: ${lbl}` : "";
            },
            label: (ctx) => {
              // Show exact timestamp from raw timestamps array (passed via ds metadata)
              const rawTs = ctx.dataset._timestamps?.[ctx.dataIndex];
              const timePart = rawTs ? moment(rawTs).format("h:mm A") : ctx.label;
              return ctx.parsed.y >= 1
                ? ` Flow detected · ${timePart}`
                : ` No water (Dry) · ${timePart}`;
            }
          }
        }
      },
      scales: {
        x: {
          grid: { display: false }, border: { display: false },
          ticks: { font: { size: 10, family: "Inter" }, color: "#4B5563", maxTicksLimit: 14 }
        },
        y: {
          min: 0, max: 1.5, grid: { color: GRAY_GRID }, border: { display: false },
          ticks: {
            font: { size: 10, family: "Inter" }, color: "#4B5563", stepSize: 1,
            callback: (v) => v === 1 ? "Flow" : v === 0 ? "Dry" : ""
          }
        },
      },
    };
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: false },
        datalabels: { display: false },
        tooltip: trendTooltipPlugin(isDayView),
      },
      scales: {
        x: {
          // Day view: labels are pre-blanked to one per hour, so show them all
          // (no index-sampling, which caused duplicate hours). Multi-day keeps
          // the sampled date ticks.
          grid: { display: false, drawTicks: !isDayView },
          border: { display: false },
          ticks: {
            font: { size: 10, family: "Inter" },
            color: isDayView
              ? (ctx) => (amPmColors && amPmColors[ctx.index] ? amPmColors[ctx.index] : "#9CA3AF")
              : "#9CA3AF",
            autoSkip: !isDayView,
            maxRotation: 0,
            maxTicksLimit: isDayView ? undefined : 14,
          },
        },
        y: {
          min: 0,
          max: 120,
          grid: { color: GRAY_GRID },
          border: { display: false },
          ticks: {
            font: { size: 10, family: "Inter" },
            color: "#4B5563",
            callback: (v) => v + "%",
            stepSize: 30,
          },
        },
      },
    };
  }

  function volOptions(ds) {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        datalabels: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const v = ctx.parsed.y;
              return ` ${literToKiloliter(v)}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          border: { display: false },
          ticks: { font: { size: 10, family: "Inter" }, color: "#4B5563" },
        },
        y: {
          grid: { color: GRAY_GRID },
          border: { display: false },
          ticks: {
            font: { size: 10, family: "Inter" }, color: "#4B5563",
            callback: (v) => literToKiloliter(v),  // literToKiloliter handles unit
          },
        },
      },
    };
  }

  /* ── Range navigator: shift date window by one period ── */
  const shiftWindow = useCallback((dir) => {
    const panel = activeTab === "trend" ? trendMode : volMode;
    if (panel === "week") {
      const days = dir === -1 ? -7 : 7;
      const newFrom = moment(tFrom).add(days, "days").format("YYYY-MM-DD");
      const newTo = moment(tTo).add(days, "days").format("YYYY-MM-DD");
      if (activeTab === "trend") { setTFrom(newFrom); setTTo(newTo); fetchChart("week", newFrom, newTo); }
      else { setVFrom(newFrom); setVTo(newTo); fetchChart("week", newFrom, newTo); }
    } else if (panel === "range") {
      const spanDays = moment(activeTab === "trend" ? tTo : vTo)
        .diff(moment(activeTab === "trend" ? tFrom : vFrom), "days") + 1;
      const days = dir * spanDays;
      if (activeTab === "trend") {
        const newFrom = moment(tFrom).add(days, "days").format("YYYY-MM-DD");
        const newTo = moment(tTo).add(days, "days").format("YYYY-MM-DD");
        setTFrom(newFrom); setTTo(newTo); fetchChart("range", newFrom, newTo);
      } else {
        const newFrom = moment(vFrom).add(days, "days").format("YYYY-MM-DD");
        const newTo = moment(vTo).add(days, "days").format("YYYY-MM-DD");
        setVFrom(newFrom); setVTo(newTo); fetchChart("range", newFrom, newTo);
      }
    }
  }, [activeTab, trendMode, volMode, tFrom, tTo, vFrom, vTo, fetchChart]);

  /* ── Active mode (per tab) ── */
  const currentMode = activeTab === "trend" ? trendMode : volMode;
  const activeFrom = activeTab === "trend" ? tFrom : vFrom;
  const activeTo = activeTab === "trend" ? tTo : vTo;
  const rangeInfo = modeRangeLabel(currentMode, activeFrom, activeTo);
  const isDayView = currentMode === "today" || currentMode === "date";

  // Tank type drives the default Volume metric. No explicit backend field yet,
  // so infer from the location name (same trick the Solenoid page uses).
  const isOHT = /oht|overhead|tower/i.test(locName || "");
  const volMetric = volMetricOverride ?? (isOHT ? "consumed" : "filled");
  const volMetricLabel = volMetric === "consumed" ? "Water consumed" : "Water filled";

  /* Build chart datasets from current API data */
  const tDs = chartData
    ? buildTrendDatasets(chartData.mode, chartData.timestamps, chartData.datas)
    : null;
  const vDs = chartData
    ? buildVolDatasets(chartData.mode, chartData.timestamps, chartData.datas, chartData.unit, tankVol, volMetric, chartData.datasLitres)
    : null;

  /* ─── RENDER ──────────────────────────────────────────────────────────── */
  if (readLoading) return <LoadingBox>Loading sensor data…</LoadingBox>;

  return (
    <Screen>
      <PullToRefresh />


      {/* NAV */}
      <Nav className="no-print" data-ptr-header>
        <BackBtn onClick={() => history.goBack()}>←</BackBtn>
        <NavInfo>
          <NavEyebrow>Water level sensor</NavEyebrow>
          <NavTitle title={locName}>{locName}</NavTitle>
        </NavInfo>
        <NavDl title="Export as PDF" onClick={handleDownload}>
          <BiDownload size={18} />
        </NavDl>
      </Nav>

      {/* FIXED HEADER */}
      <FixedHeader>
        <HeroSplit>
          {/* Gauge */}
          <GaugeCol>
            <GaugeWrap>
              <GaugeSvgComponent pct={pct} minPct={thresholds.min} maxPct={thresholds.max} />
              <GaugeCenter>
                {isHydroSense ? (
                  <GaugePct style={{ fontSize: 22 }}>
                    {hydroValue >= 1 ? "FLOW" : "DRY"}
                  </GaugePct>
                ) : (
                  <GaugePct>{Math.round(pct)}<span>%</span></GaugePct>
                )}
                <GaugeDot $status={status} />
                <GaugeStatus $status={status}>
                  {status === "online" ? "Live" : status}
                </GaugeStatus>
              </GaugeCenter>
            </GaugeWrap>
            <GaugeLegend>
              <LegendItem><LegendDot $color={GREEN} />Level</LegendItem>
              <LegendItem><LegendDot $color={RED_ALERT} />Min</LegendItem>
              <LegendItem><LegendDot $color={AMBER} />Max</LegendItem>
            </GaugeLegend>
          </GaugeCol>

          {/* Stats */}
          <StatsCol>
            {isHydroSense ? (
              /* HydroSense: binary sensor — presence status + editable wait time */
              <>
                <StatItem style={{ padding: "12px 0" }}>
                  <StatDot $color={hydroValue >= 1 ? GREEN : "#94A3B8"} />
                  <StatLabel>Water present</StatLabel>
                  <StatValue style={{
                    color: hydroValue >= 1 ? GREEN : "#94A3B8",
                    fontSize: 15,
                    fontWeight: 700
                  }}>
                    {hydroValue >= 1 ? "Yes — Flow detected" : "No — Dry"}
                  </StatValue>
                </StatItem>
                {dimension && (
                  <StatItem style={{ padding: "12px 0", gap: 10 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#374151", whiteSpace: "nowrap" }}>
                      Wait time (mins)
                    </span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <input
                        type="number" min="0" max="999"
                        value={editWaitTime}
                        onChange={(e) => setEditWaitTime(clampWait(e.target.value))}
                        style={{
                          width: 56, height: 30, textAlign: "center",
                          border: "1.5px solid #E5E7EB", borderRadius: 8,
                          fontSize: 13, fontWeight: 600, fontFamily: "inherit",
                          background: "#F9FAFB", color: "#111", outline: "none",
                        }}
                      />
                      <button
                        onClick={saveWaitTime}
                        disabled={!waitDirty || savingWait}
                        style={{
                          height: 30, padding: "0 12px", borderRadius: 8, border: "none",
                          fontSize: 13, fontWeight: 600, fontFamily: "inherit",
                          background: (waitDirty && !savingWait) ? GREEN : "#E5E7EB",
                          color: (waitDirty && !savingWait) ? "#fff" : "#9CA3AF",
                          cursor: (waitDirty && !savingWait) ? "pointer" : "default",
                        }}
                      >
                        {savingWait ? "…" : "Save"}
                      </button>
                      {waitMsg && (
                        <span style={{ fontSize: 12, fontWeight: 600, color: waitErr ? "#DC2626" : GREEN }}>
                          {waitMsg}
                        </span>
                      )}
                    </div>
                  </StatItem>
                )}
              </>
            ) : (
              /* Standard water-level stats */
              <>
                <StatItem>
                  <StatDot $color={GREEN} />
                  <StatLabel>Volume filled</StatLabel>
                  <StatValue>{literToKiloliter(volFilled)}</StatValue>
                </StatItem>
                <StatItem>
                  <StatDot $color="#D1D5DB" />
                  <StatLabel>Tank capacity</StatLabel>
                  <StatValue>
                    {tankVol > 0 ? literToKiloliter(tankVol) : <span style={{ color: "#4B5563" }}>—</span>}
                  </StatValue>
                </StatItem>
                <StatItem>
                  <StatDot $color="#9CA3AF" />
                  <StatLabel>Shape</StatLabel>
                  <StatValue>{tankShape || "—"}</StatValue>
                </StatItem>
                {tankVol > 0 && (
                  <StatItem>
                    <StatDot $color="#2563EB" />
                    <StatLabel>To fill</StatLabel>
                    <StatValue>{literToKiloliter(toFill)}</StatValue>
                  </StatItem>
                )}

                {/* Min threshold — inline editable */}
                <StatItem>
                  <StatDot $color={RED_ALERT} />
                  <StatLabel>Min alert</StatLabel>
                  {!editMin ? (
                    <ThresholdView onClick={() => { setEditMinVal(String(thresholds.min)); setEditMin(true); setEditMax(false); }}>
                      <StatValue>{thresholds.min}<span>%</span></StatValue>
                      <EditHint className="edit-hint">edit</EditHint>
                    </ThresholdView>
                  ) : (
                    <ThresholdEditor $open>
                      <ThresholdInput
                        type="number" min="0" max="100"
                        value={editMinVal}
                        onChange={(e) => setEditMinVal(e.target.value)}
                        autoFocus
                      />
                      <span style={{ fontSize: 10, color: "#4B5563" }}>%</span>
                      <CancelBtn onClick={() => setEditMin(false)}>✕</CancelBtn>
                      <SaveBtn onClick={() => saveThreshold("min")}>Save</SaveBtn>
                    </ThresholdEditor>
                  )}
                </StatItem>

                {/* Max threshold — inline editable */}
                <StatItem>
                  <StatDot $color={AMBER} />
                  <StatLabel>Max alert</StatLabel>
                  {!editMax ? (
                    <ThresholdView onClick={() => { setEditMaxVal(String(thresholds.max)); setEditMax(true); setEditMin(false); }}>
                      <StatValue>{thresholds.max}<span>%</span></StatValue>
                      <EditHint className="edit-hint">edit</EditHint>
                    </ThresholdView>
                  ) : (
                    <ThresholdEditor $open>
                      <ThresholdInput
                        type="number" min="0" max="100"
                        value={editMaxVal}
                        onChange={(e) => setEditMaxVal(e.target.value)}
                        autoFocus
                      />
                      <span style={{ fontSize: 10, color: "#4B5563" }}>%</span>
                      <CancelBtn onClick={() => setEditMax(false)}>✕</CancelBtn>
                      <SaveBtn onClick={() => saveThreshold("max")}>Save</SaveBtn>
                    </ThresholdEditor>
                  )}
                </StatItem>

              </>
            )}
          </StatsCol>
        </HeroSplit>

        {/* Status bar */}
        <StatusBar>
          <OnlinePill $status={status}>
            <OnlineDot $status={status} />
            <OnlineText $status={status}>
              {status === "online" ? "Live" : status === "faulty" ? "Faulty" : "Offline"}
            </OnlineText>
          </OnlinePill>
          <SyncText>
            Synced <span>{lastSync}</span>
          </SyncText>
        </StatusBar>
      </FixedHeader>

      {/* TAB BAR */}
      <TabBar className="no-print">
        <Tab $active={activeTab === "trend"} onClick={() => { setActiveTab("trend"); if (chartData?.mode !== trendMode) fetchChart(trendMode, tFrom, tTo); }}>
          Trend %
        </Tab>
        {!isHydroSense && (
          <Tab $active={activeTab === "volume"} onClick={() => { setActiveTab("volume"); if (chartData?.mode !== volMode) fetchChart(volMode, vFrom, vTo); }}>
            Volume
          </Tab>
        )}
      </TabBar>

      {/* ── BODY ── */}
      <Body>

        {/* ═══ TREND PANEL ═══ */}
        {activeTab === "trend" && (
          <>
            <FilterRow className="no-print">
              <PillTabs>
                {["today", "date", "range"].map(m => (
                  <PillTab key={m} $active={trendMode === m} onClick={() => handleTrendMode(m)}>
                    {m.charAt(0).toUpperCase() + m.slice(1)}
                  </PillTab>
                ))}
              </PillTabs>
              <DateLabel>{modeDateLabel(trendMode, trendMode === "date" ? tSingleDate : tFrom, tTo)}</DateLabel>
            </FilterRow>

            {/* Single date picker — shown when Date tab is active */}
            <RangePicker className="no-print" $open={trendMode === "date"}>
              <RpLabel>Date</RpLabel>
              <RpInput
                type="date"
                value={tSingleDate}
                max={today}
                onChange={e => setTSingleDate(e.target.value)}
              />
              <RpApply onClick={() => fetchChart("date", tSingleDate, tSingleDate)}>Apply</RpApply>
            </RangePicker>

            {/* Range picker */}
            <RangePicker className="no-print" $open={trendMode === "range"}>
              <RpLabel>From</RpLabel>
              <RpInput type="date" value={tFrom} onChange={e => setTFrom(e.target.value)} />
              <span style={{ fontSize: 12, color: "#4B5563", fontWeight: 600 }}>→</span>
              <RpLabel>To</RpLabel>
              <RpInput type="date" value={tTo} onChange={e => setTTo(e.target.value)} />
              <RpApply onClick={() => fetchChart("range", tFrom, tTo)}>Apply</RpApply>
            </RangePicker>

            {/* Range navigator */}
            {rangeInfo && activeTab === "trend" && (
              <RangeStrip className="no-print" $visible>
                <RangeArrow onClick={() => shiftWindow(-1)}>‹</RangeArrow>
                <RangeCenter>
                  <RangeLbl>{rangeInfo.label}</RangeLbl>
                  <RangeSub>{rangeInfo.sub}</RangeSub>
                </RangeCenter>
                <RangeArrow onClick={() => shiftWindow(1)}>›</RangeArrow>
              </RangeStrip>
            )}

            {/* Chart — HydroSense multi-day: show timeline; otherwise standard chart */}
            {isHydroSense && !isDayView ? (
              <>
                <ChartCard>
                  <ChartHeader>
                    <div>
                      <ChartTitle>Water presence timeline</ChartTitle>
                      <ChartSub>Hourly flow/dry · {modeDateLabel(trendMode, trendMode === "date" ? tSingleDate : tFrom, tTo).replace("📅 ", "")}</ChartSub>
                    </div>
                    <KpiChip>
                      <KpiVal style={{ fontSize: 14 }}>{hydroValue >= 1 ? "FLOW" : "DRY"}</KpiVal>
                      <KpiLbl>Now</KpiLbl>
                    </KpiChip>
                  </ChartHeader>
                </ChartCard>
                {chartData && (
                  <HydroTimeline
                    timestamps={chartData.timestamps}
                    datas={chartData.datas}
                  />
                )}
              </>
            ) : (
              <ChartCard>
                <ChartHeader>
                  <div>
                    <ChartTitle>{isHydroSense ? "Water presence" : isDayView ? "Water level trend" : "Daily avg level"}</ChartTitle>
                    <ChartSub>
                      {isDayView ? `% over time · ${modeDateLabel(trendMode, trendMode === "date" ? tSingleDate : tFrom, tTo).replace("📅 ", "")}` : `% per day · ${modeDateLabel(trendMode, trendMode === "date" ? tSingleDate : tFrom, tTo).replace("📅 ", "")}`}
                    </ChartSub>
                  </div>
                  <KpiChip>
                    {isHydroSense ? (
                      <>
                        <KpiVal style={{ fontSize: 14 }}>{hydroValue >= 1 ? "FLOW" : "DRY"}</KpiVal>
                        <KpiLbl>Now</KpiLbl>
                      </>
                    ) : (
                      <>
                        <KpiVal>{Math.round(pct)}<span>%</span></KpiVal>
                        <KpiLbl>{isDayView ? "Current" : "Now"}</KpiLbl>
                      </>
                    )}
                  </KpiChip>
                </ChartHeader>
                <ChartWrap>
                  {chartLoading ? (
                    <LoadingBox>Loading chart…</LoadingBox>
                  ) : tDs ? (
                    <Line
                      data={{ labels: tDs.labels, datasets: tDs.datasets }}
                      options={trendOptions(tDs.isDayView, tDs.amPmColors, tDs.isHydro, tDs.amPmColors)}
                    />
                  ) : (
                    <LoadingBox>No data for this period</LoadingBox>
                  )}
                </ChartWrap>
                <AmPmStrip $visible={isDayView && !!tDs}>
                  <AmLabel>● AM</AmLabel>
                  {/* On "Today", only show PM once the clock passes noon */}
                  <PmLabel>{(trendMode !== "today" || moment().hour() >= 12) ? "● PM" : ""}</PmLabel>
                </AmPmStrip>
              </ChartCard>
            )}

            {/* Insight chips */}
            {!isHydroSense && (
              <InsightsSection className="no-print">
                <InsightsLbl>
                  {isDayView ? "Day insights" : "Period insights"}
                </InsightsLbl>
                <InsightChips chips={trendChips(trendMode, tDs)} />
              </InsightsSection>
            )}

            {/* Summary table for multi-day */}
            {!isDayView && tDs && <TrendTable ds={tDs} />}

            <Spacer />
          </>
        )}

        {/* ═══ VOLUME PANEL ═══ */}
        {activeTab === "volume" && (
          <>
            <FilterRow className="no-print">
              <PillTabs>
                {["today", "date", "range"].map(m => (
                  <PillTab key={m} $active={volMode === m} onClick={() => handleVolMode(m)}>
                    {m.charAt(0).toUpperCase() + m.slice(1)}
                  </PillTab>
                ))}
              </PillTabs>
              <DateLabel>{modeDateLabel(volMode, volMode === "date" ? vSingleDate : vFrom, vTo)}</DateLabel>
            </FilterRow>

            {/* Single date picker — shown when Date tab is active */}
            <RangePicker className="no-print" $open={volMode === "date"}>
              <RpLabel>Date</RpLabel>
              <RpInput
                type="date"
                value={vSingleDate}
                max={today}
                onChange={e => setVSingleDate(e.target.value)}
              />
              <RpApply onClick={() => fetchChart("date", vSingleDate, vSingleDate)}>Apply</RpApply>
            </RangePicker>

            {/* Range picker */}
            <RangePicker className="no-print" $open={volMode === "range"}>
              <RpLabel>From</RpLabel>
              <RpInput type="date" value={vFrom} onChange={e => setVFrom(e.target.value)} />
              <span style={{ fontSize: 12, color: "#4B5563", fontWeight: 600 }}>→</span>
              <RpLabel>To</RpLabel>
              <RpInput type="date" value={vTo} onChange={e => setVTo(e.target.value)} />
              <RpApply onClick={() => fetchChart("range", vFrom, vTo)}>Apply</RpApply>
            </RangePicker>

            {/* Range navigator */}
            {modeRangeLabel(volMode, vFrom, vTo) && (
              <RangeStrip className="no-print" $visible>
                <RangeArrow onClick={() => shiftWindow(-1)}>‹</RangeArrow>
                <RangeCenter>
                  <RangeLbl>{modeRangeLabel(volMode, vFrom, vTo)?.label}</RangeLbl>
                  <RangeSub>{modeRangeLabel(volMode, vFrom, vTo)?.sub}</RangeSub>
                </RangeCenter>
                <RangeArrow onClick={() => shiftWindow(1)}>›</RangeArrow>
              </RangeStrip>
            )}

            {/* Chart */}
            <ChartCard>
              <ChartHeader>
                <div>
                  <ChartTitle>{volMetricLabel} {isDayView ? "per slot" : "per day"}</ChartTitle>
                  <ChartSub>
                    {isDayView
                      ? `Net ${volMetric} · per 2-hr window · ${modeDateLabel(volMode, volMode === "date" ? vSingleDate : vFrom, vTo).replace("📅 ", "")}`
                      : `Net ${volMetric} · per day · ${modeDateLabel(volMode, volMode === "date" ? vSingleDate : vFrom, vTo).replace("📅 ", "")}`}
                  </ChartSub>
                </div>
                <KpiChip>
                  <KpiVal>
                    {vDs?.total ? literToKiloliter(parseFloat(vDs.total)) : "—"}
                  </KpiVal>
                  <KpiLbl>{volMetric === "consumed" ? "Consumed" : "Filled"} total</KpiLbl>
                </KpiChip>
              </ChartHeader>
              {/* Filled / Consumed toggle — defaults per tank (Sump→Filled, OHT→Consumed) */}
              <PillTabs className="no-print" style={{ marginBottom: 10 }}>
                <PillTab $active={volMetric === "filled"} onClick={() => setVolMetricOverride("filled")}>Filled</PillTab>
                <PillTab $active={volMetric === "consumed"} onClick={() => setVolMetricOverride("consumed")}>Consumed</PillTab>
              </PillTabs>
              <ChartWrap>
                {chartLoading ? (
                  <LoadingBox>Loading chart…</LoadingBox>
                ) : vDs ? (
                  <Bar
                    data={{ labels: vDs.labels, datasets: vDs.datasets }}
                    options={volOptions(vDs)}
                  />
                ) : (
                  <LoadingBox>No data for this period</LoadingBox>
                )}
              </ChartWrap>
              <AmPmStrip $visible={(volMode === "today" || volMode === "date") && !!vDs}>
                <AmLabel>● AM</AmLabel>
                {/* On "Today", only show PM once the clock passes noon */}
                <PmLabel>{(volMode !== "today" || moment().hour() >= 12) ? "● PM" : ""}</PmLabel>
              </AmPmStrip>
            </ChartCard>

            {/* Insight chips */}
            <InsightsSection className="no-print">
              <InsightsLbl>
                {(volMode === "today" || volMode === "date") ? "Day insights" : "Period insights"}
              </InsightsLbl>
              <InsightChips chips={volChips(vDs)} />
            </InsightsSection>

            {/* Summary table for multi-day */}
            {!(volMode === "today" || volMode === "date") && vDs && <VolTable ds={vDs} />}

            <Spacer />
          </>
        )}

      </Body>
    </Screen>
  );
}